<template>
  <div class="page" :class="{ embedded: embedded }">
    <div class="card" style="max-width: 820px;">
      <div class="card-header">
        <div>
          <h1 class="title">Documento de seguranca</h1>
          <p class="subtitle">Visualizacao detalhada do formulario.</p>
        </div>
        <span class="pill">{{ submission?.title || 'Seguranca' }}</span>
      </div>

      <div v-if="!submission" class="item-card">
        <h3 class="item-title">Documento nao encontrado</h3>
        <p class="item-meta">Volte e selecione outro item.</p>
      </div>

      <div v-else class="grid" style="gap: 16px;">
        <div class="item-card">
          <div class="item-title">{{ submission.title }} - {{ submission.attraction }}</div>
          <div class="item-meta">Data: {{ formatReportDate(submission.reportDate) }}</div>
          <div class="item-meta">Autor: {{ submission.author.name }}</div>
        </div>

        <div v-if="isDialogo" class="item-card">
          <div class="item-title">Tema do dialogo</div>
          <div class="item-meta">{{ dialogTheme || 'Nao informado' }}</div>
          <div class="item-title" style="margin-top: 12px;">Participantes</div>
          <div class="item-meta">{{ participantsText }}</div>
        </div>

        <div v-else-if="isOcorrido" class="item-card">
          <div class="item-title">Descricao do ocorrido</div>
          <div class="item-meta">{{ occurrenceText || 'Nao informado' }}</div>
          <div class="item-title" style="margin-top: 12px;">Imagens</div>
          <div v-if="occurrenceImages.length" class="image-grid" style="margin-top: 8px;">
            <div
              v-for="(img, index) in occurrenceImages"
              :key="`occ-img-${index}`"
              class="image-preview"
            >
              <img :src="resolveUrl(img)" alt="preview" />
            </div>
          </div>
          <div v-else class="item-meta">Sem imagens</div>
        </div>

        <div v-else class="item-card">
          <div class="item-title">Permissao de trabalho critico</div>
          <div class="item-meta">Documento registrado, sem campos adicionais neste visualizador.</div>
        </div>

        <div class="actions">
          <button class="btn btn-ghost" type="button" @click="back">Voltar</button>
          <button class="btn btn-secondary" type="button" @click="edit">Editar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { submissionsStore } from '@/store/submissions'
import { formatDateOnly } from '@/utils/date'
import { resolveUrl } from '@/utils/resolveUrl'

export default defineComponent({
  name: 'SafetyDocumentView',
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    }
  },
  emits: ['back', 'edit'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()

    const submission = computed(() => {
      const id = props.submissionId || (route.params.id as string)
      return submissionsStore.getById(id)
    })

    const isDialogo = computed(() => submission.value?.title === 'Dialogos de seguranca')
    const isOcorrido = computed(() => submission.value?.title === 'Relatorio de ocorrido')

    const dialogTheme = computed(() => submission.value?.observations?.[0] || '')
    const participantsText = computed(() =>
      submission.value?.collaborators?.length ? submission.value.collaborators.join(', ') : 'Sem participantes'
    )
    const occurrenceText = computed(() => submission.value?.observations?.[0] || '')
    const occurrenceImages = computed(() => submission.value?.activities?.[0]?.images || [])

    const back = () => {
      if (props.embedded) {
        emit('back')
      } else {
        navigationStore.goBack(router, '/safety')
      }
    }
    const edit = () => {
      const item = submission.value
      if (!item) return
      if (props.embedded) {
        emit('edit', item.id)
        return
      }
      if (item.category === 'dialogo') {
        router.push(`/safety/dialogo/${item.id}`)
        return
      }
      if (item.category === 'ocorrido') {
        router.push(`/safety/ocorrido/${item.id}`)
        return
      }
      router.push(`/submission/${item.id}/edit`)
    }

    onMounted(() => {
      submissionsStore.refresh().catch(() => {
        // ignore for now
      })
    })

    return {
      submission,
      isDialogo,
      isOcorrido,
      dialogTheme,
      participantsText,
      occurrenceText,
      occurrenceImages,
      resolveUrl,
      formatReportDate: formatDateOnly,
      back,
      edit
    }
  }
})
</script>

<style scoped lang="scss">
.page.embedded {
  min-height: auto;
  padding: 18px 16px 28px;
  align-items: flex-start;
}

.page.embedded .card {
  max-width: none !important;
  width: 100%;
  padding: 16px;
  box-shadow: none;
  background: transparent;
  border-radius: 0;
}

@media (max-width: 760px) {
  .page.embedded {
    padding: 14px 12px 22px;
  }

  .page.embedded .card {
    padding: 12px;
  }
}
</style>
